// src/pages/TimbratureGestione.jsx
//
// Controllo delle timbrature per admin, gestione e segreteria: per ogni
// tecnico le giornate del periodo con entrata, pausa, uscita, ore lavorate,
// straordinari e anomalie; posizione di ogni timbratura su Google Maps;
// inserimento a mano e cancellazione; export Excel per le paghe.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { authFetch } from '../utils/authFetch';
import { Badge, Card } from '../components/ui';

const API_BASE = '/api';

const TYPE_LABEL = {
  entrata: 'Entrata',
  pausa_inizio: 'Inizio pausa',
  pausa_fine: 'Fine pausa',
  uscita: 'Uscita',
};

const FLAG_LABEL = {
  incompleta: 'incompleta',
  in_ritardo: 'orario a mano',
  senza_posizione: 'senza posizione',
  pausa_non_chiusa: 'pausa non chiusa',
  festivo: 'festivo',
};

const fmtMin = (m) => {
  if (m === null || m === undefined) return '-';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${String(mm).padStart(2, '0')}m`;
};

const fmtDay = (day) => {
  const d = new Date(`${day}T00:00:00`);
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const userLabel = (u) => {
  if (!u) return '';
  const full = `${u.nome || ''} ${u.cognome || ''}`.trim();
  return full || u.username || '';
};

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const timeOf = (day, type) => day.entries.find((e) => e.type === type)?.time || '';

export default function TimbratureGestione() {
  const navigate = useNavigate();
  const role = localStorage.getItem('role') || '';
  const isManager = ['admin', 'segreteria', 'gestione'].includes(role);

  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [userFilter, setUserFilter] = useState('');
  const [soloAnomalie, setSoloAnomalie] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [openDay, setOpenDay] = useState(null); // `${userId}|${day}`
  const [manual, setManual] = useState(null); // { userId, day, type, time, note }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ from, to });
      if (userFilter) qs.set('user', userFilter);
      const res = await authFetch(`${API_BASE}/timbrature?${qs}`);
      if (!res || !res.ok) throw new Error('Errore nel caricamento delle timbrature');
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, userFilter]);

  useEffect(() => {
    if (isManager) load();
  }, [isManager, load]);

  const allUsers = useMemo(() => (data?.users || []).map((u) => u.user), [data]);

  const elimina = async (entry) => {
    if (!window.confirm(`Cancello ${TYPE_LABEL[entry.type]} delle ${entry.time}?`)) return;
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/timbrature/${entry._id}`, { method: 'DELETE' });
      const d = await res.json().catch(() => null);
      if (!res.ok) throw new Error(d?.error || 'Errore nella cancellazione');
      setNotice('Timbratura cancellata.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const salvaManuale = async () => {
    if (!manual.time || !manual.note.trim()) {
      setError('Servono orario e nota.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/timbrature/manuale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(manual),
      });
      const d = await res.json().catch(() => null);
      if (!res.ok) throw new Error(d?.error || 'Errore nel salvataggio');
      setNotice('Timbratura inserita.');
      setManual(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const exportExcel = () => {
    if (!data) return;
    const rows = [['Tecnico', 'Data', 'Entrata', 'Inizio pausa', 'Fine pausa', 'Uscita', 'Ore lavorate', 'Straordinario', 'Anomalie', 'Note']];
    for (const u of data.users) {
      for (const d of u.days) {
        if (!d.entries.length && !d.leaveType) continue;
        const s = d.summary;
        rows.push([
          userLabel(u.user),
          new Date(`${d.day}T00:00:00`),
          timeOf(d, 'entrata'),
          timeOf(d, 'pausa_inizio'),
          timeOf(d, 'pausa_fine'),
          timeOf(d, 'uscita'),
          s.workedMinutes == null ? '' : Number((s.workedMinutes / 60).toFixed(2)),
          s.overtimeMinutes ? Number((s.overtimeMinutes / 60).toFixed(2)) : 0,
          [...(d.leaveType ? [d.leaveType] : []), ...s.flags.map((f) => FLAG_LABEL[f] || f)].join(', '),
          d.entries.filter((e) => e.note).map((e) => `${TYPE_LABEL[e.type]}: ${e.note}`).join(' | '),
        ]);
      }
      rows.push([`Totale ${userLabel(u.user)}`, '', '', '', '', '', Number(((u.totals.workedMinutes || 0) / 60).toFixed(2)), Number(((u.totals.overtimeMinutes || 0) / 60).toFixed(2)), '', '']);
    }
    const ws = XLSX.utils.aoa_to_sheet(rows, { dateNF: 'dd/mm/yyyy' });
    ws['!cols'] = [22, 12, 9, 11, 11, 9, 12, 12, 28, 50].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timbrature');
    XLSX.writeFile(wb, `timbrature_${from}_${to}.xlsx`);
  };

  if (!isManager) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-100 px-4">
        <Card className="shadow-md p-6 max-w-md w-full text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Accesso non autorizzato</h2>
          <p className="text-sm text-gray-600 mb-4">Solo Admin, Gestione e Segreteria vedono le timbrature del personale.</p>
          <button onClick={() => navigate('/timbrature')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">Le mie timbrature</button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-6xl space-y-4">
        <Card className="shadow-sm px-4 py-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Timbrature del personale</h1>
              <p className="text-xs md:text-sm text-gray-500">
                Ore lavorate, pause e straordinari oltre le {data ? Math.round(data.standardMinutes / 60) : 8} ore; sabato, domenica e chiusure contano tutte come straordinario.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/timbrature')} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200">Le mie timbrature</button>
              <button onClick={() => navigate('/dashboard')} className="hidden md:inline-flex px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200">← Dashboard</button>
            </div>
          </div>
        </Card>

        <Card className="shadow-sm px-3 py-2">
          <div className="flex flex-wrap items-end gap-2 text-xs">
            <label className="text-gray-600">Dal<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block mt-0.5 px-2 py-1.5 border rounded-lg bg-white" /></label>
            <label className="text-gray-600">Al<input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block mt-0.5 px-2 py-1.5 border rounded-lg bg-white" /></label>
            <label className="text-gray-600">
              Persona
              <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="block mt-0.5 px-2 py-1.5 border rounded-lg bg-white min-w-[10rem]">
                <option value="">tutti</option>
                {allUsers.map((u) => (
                  <option key={u._id} value={u._id}>{userLabel(u)}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-gray-600 pb-2">
              <input type="checkbox" checked={soloAnomalie} onChange={(e) => setSoloAnomalie(e.target.checked)} /> solo giornate con anomalie
            </label>
            <button type="button" onClick={exportExcel} disabled={!data} className="ml-auto px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50">Scarica Excel</button>
          </div>
        </Card>

        {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 flex justify-between"><span>{error}</span><button type="button" onClick={() => setError('')} className="text-xs underline">chiudi</button></div>}
        {notice && <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800 flex justify-between"><span>{notice}</span><button type="button" onClick={() => setNotice('')} className="text-xs underline">chiudi</button></div>}

        {loading || !data ? (
          <Card className="shadow-sm p-6 text-sm text-gray-500 text-center">Caricamento...</Card>
        ) : data.users.length === 0 ? (
          <Card className="shadow-sm p-6 text-sm text-gray-500 text-center">Nessuna persona da mostrare.</Card>
        ) : (
          data.users.map((u) => {
            const days = u.days.filter((d) => {
              if (soloAnomalie) return d.summary.flags.some((f) => f !== 'festivo');
              return d.entries.length || d.leaveType || d.workingDay;
            });
            return (
              <Card key={u.user._id} className="shadow-sm p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-800">{userLabel(u.user)} <span className="text-xs text-gray-400 font-normal">{u.user.role}</span></div>
                    <div className="text-xs text-gray-500">
                      {u.totals.giorniTimbrati || 0} giornate · {fmtMin(u.totals.workedMinutes || 0)} lavorate
                      {u.totals.overtimeMinutes > 0 && <span className="text-indigo-700"> · {fmtMin(u.totals.overtimeMinutes)} straordinario</span>}
                      {u.totals.anomalie > 0 && <span className="text-amber-700"> · {u.totals.anomalie} con anomalie</span>}
                    </div>
                  </div>
                  <button type="button" onClick={() => setManual({ userId: u.user._id, day: to, type: 'entrata', time: '', note: '' })} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200">
                    Inserisci timbratura
                  </button>
                </div>

                {manual && manual.userId === u.user._id && (
                  <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 grid gap-2 sm:grid-cols-5 text-xs">
                    <label className="text-gray-600">Giorno<input type="date" value={manual.day} max={today()} onChange={(e) => setManual((m) => ({ ...m, day: e.target.value }))} className="block mt-0.5 w-full px-2 py-1.5 border rounded-lg bg-white" /></label>
                    <label className="text-gray-600">Cosa<select value={manual.type} onChange={(e) => setManual((m) => ({ ...m, type: e.target.value }))} className="block mt-0.5 w-full px-2 py-1.5 border rounded-lg bg-white">{Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
                    <label className="text-gray-600">Ora<input type="time" value={manual.time} onChange={(e) => setManual((m) => ({ ...m, time: e.target.value }))} className="block mt-0.5 w-full px-2 py-1.5 border rounded-lg bg-white" /></label>
                    <label className="text-gray-600 sm:col-span-2">Nota (obbligatoria)<input type="text" value={manual.note} onChange={(e) => setManual((m) => ({ ...m, note: e.target.value }))} placeholder="es. dimenticata, confermata a voce" className="block mt-0.5 w-full px-2 py-1.5 border rounded-lg bg-white" /></label>
                    <div className="sm:col-span-5 flex gap-2 justify-end">
                      <button type="button" disabled={busy} onClick={() => setManual(null)} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 font-medium">Annulla</button>
                      <button type="button" disabled={busy} onClick={salvaManuale} className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-medium disabled:opacity-50">Salva</button>
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-1.5 pr-2">Giorno</th>
                        <th className="py-1.5 pr-2">Entrata</th>
                        <th className="py-1.5 pr-2">Pausa</th>
                        <th className="py-1.5 pr-2">Uscita</th>
                        <th className="py-1.5 pr-2">Ore</th>
                        <th className="py-1.5 pr-2">Straord.</th>
                        <th className="py-1.5 pr-2">Anomalie</th>
                        <th className="py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {days.map((d) => {
                        const s = d.summary;
                        const key = `${u.user._id}|${d.day}`;
                        const vuoto = !d.entries.length;
                        const isOpen = openDay === key;
                        return (
                          <React.Fragment key={d.day}>
                            <tr className={`border-b border-gray-100 ${vuoto ? 'text-gray-400' : 'text-gray-800'} ${!d.workingDay ? 'bg-gray-50' : ''}`}>
                              <td className="py-1.5 pr-2 whitespace-nowrap">{fmtDay(d.day)}</td>
                              {vuoto ? (
                                <td colSpan={6} className="py-1.5 italic">
                                  {d.leaveType ? d.leaveType : d.holiday ? 'chiusura aziendale' : d.workingDay ? 'nessuna timbratura' : ''}
                                </td>
                              ) : (
                                <>
                                  <td className="py-1.5 pr-2">{timeOf(d, 'entrata') || '-'}</td>
                                  <td className="py-1.5 pr-2 whitespace-nowrap">{timeOf(d, 'pausa_inizio') || '-'}{timeOf(d, 'pausa_fine') ? ` → ${timeOf(d, 'pausa_fine')}` : ''}</td>
                                  <td className="py-1.5 pr-2">{timeOf(d, 'uscita') || '-'}</td>
                                  <td className="py-1.5 pr-2 whitespace-nowrap">{fmtMin(s.workedMinutes)}</td>
                                  <td className="py-1.5 pr-2 whitespace-nowrap text-indigo-700">{s.overtimeMinutes ? fmtMin(s.overtimeMinutes) : ''}</td>
                                  <td className="py-1.5 pr-2"><div className="flex flex-wrap gap-1">{s.flags.map((f) => <Badge key={f} className={f === 'festivo' ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'}>{FLAG_LABEL[f] || f}</Badge>)}</div></td>
                                </>
                              )}
                              <td className="py-1.5 text-right">
                                {!vuoto && (
                                  <button type="button" onClick={() => setOpenDay(isOpen ? null : key)} className="text-sky-700 underline">{isOpen ? 'chiudi' : 'dettagli'}</button>
                                )}
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-gray-50">
                                <td colSpan={8} className="p-2">
                                  <ul className="space-y-1">
                                    {d.entries.map((e) => (
                                      <li key={e._id} className="flex flex-wrap items-center gap-2 text-xs">
                                        <span className="font-medium w-24">{TYPE_LABEL[e.type]}</span>
                                        <span className="font-mono">{e.time}</span>
                                        {e.position && !e.position.missing && e.position.lat != null ? (
                                          <a href={`https://www.google.com/maps?q=${e.position.lat},${e.position.lng}`} target="_blank" rel="noreferrer" className="text-sky-700 underline">
                                            posizione{e.position.accuracy ? ` (±${e.position.accuracy} m)` : ''}
                                          </a>
                                        ) : (
                                          <Badge className="bg-red-100 text-red-700">senza posizione{e.position?.reason ? `: ${e.position.reason}` : ''}</Badge>
                                        )}
                                        {e.late && <Badge className="bg-amber-100 text-amber-700">orario a mano</Badge>}
                                        {e.source === 'offline' && <Badge className="bg-gray-100 text-gray-600">inviata dopo</Badge>}
                                        {e.source === 'automatico' && (<Badge className="bg-sky-100 text-sky-700">timbratura automatica</Badge>)}
                                        {e.source === 'manuale' && <Badge className="bg-indigo-100 text-indigo-700">inserita da {userLabel(e.createdBy) || 'un responsabile'}</Badge>}
                                        {e.note && <span className="text-gray-600">“{e.note}”</span>}
                                        <button type="button" disabled={busy} onClick={() => elimina(e)} className="ml-auto text-red-700 underline">cancella</button>
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
